import { useState } from "react";
import { Slider } from "@/components/ui/slider";

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const RoiCalculator = () => {
  const [ros, setRos] = useState(25);
  const [cut, setCut] = useState(540);
  const [fightShare, setFightShare] = useState(20);

  const walkingAway = ros * cut * (1 - fightShare / 100);
  const recovered = walkingAway * 0.7;
  const yearly = recovered * 12;
  const hours = Math.round(ros * 2 * (1 - fightShare / 100) * 1.0 + ros * 0.05);

  return (
    <section
      data-testid="roi-calculator-section"
      className="py-20 sm:py-28 bg-[#FAF8F5] border-b border-[#E7E5E4]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="mb-12 max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-widest text-[#9A3412]">
            Do the math
          </span>
          <h2 className="text-3xl sm:text-4xl font-normal text-stone-900 font-serif-display mt-2">
            What are denials costing you?
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6 space-y-10">
            <div>
              <div className="flex justify-between items-baseline mb-3">
                <label className="text-xs font-bold uppercase tracking-wider text-stone-600">
                  Repair orders with a short pay / month
                </label>
                <span
                  data-testid="roi-ros-value"
                  className="text-lg font-serif-display text-stone-900"
                >
                  {ros}
                </span>
              </div>
              <Slider
                data-testid="roi-ros-slider"
                value={[ros]}
                onValueChange={([v]) => setRos(v)}
                min={5}
                max={100}
                step={1}
              />
            </div>
            <div>
              <div className="flex justify-between items-baseline mb-3">
                <label className="text-xs font-bold uppercase tracking-wider text-stone-600">
                  Average amount cut per RO
                </label>
                <span
                  data-testid="roi-cut-value"
                  className="text-lg font-serif-display text-stone-900"
                >
                  {fmt(cut)}
                </span>
              </div>
              <Slider
                data-testid="roi-cut-slider"
                value={[cut]}
                onValueChange={([v]) => setCut(v)}
                min={100}
                max={2000}
                step={10}
              />
            </div>
            <div>
              <div className="flex justify-between items-baseline mb-3">
                <label className="text-xs font-bold uppercase tracking-wider text-stone-600">
                  Share of those you currently fight
                </label>
                <span
                  data-testid="roi-fight-value"
                  className="text-lg font-serif-display text-stone-900"
                >
                  {fightShare}%
                </span>
              </div>
              <Slider
                data-testid="roi-fight-slider"
                value={[fightShare]}
                onValueChange={([v]) => setFightShare(v)}
                min={0}
                max={100}
                step={5}
              />
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="rounded-xl border border-stone-300 bg-white p-8 shadow-md">
              <div
                data-testid="roi-yearly-figure"
                className="text-4xl sm:text-5xl font-normal text-[#9A3412] font-serif-display"
              >
                {fmt(yearly)}
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mt-2">
                Recoverable per year with Brim
              </div>
              <div className="mt-8 space-y-3 text-sm text-stone-700 border-t border-stone-200 pt-6">
                <div className="flex justify-between">
                  <span>Currently walking away from</span>
                  <strong data-testid="roi-walking-away">{fmt(walkingAway)} /mo</strong>
                </div>
                <div className="flex justify-between">
                  <span>Incremental recovery (70% win rate)</span>
                  <strong data-testid="roi-monthly-recovery">{fmt(recovered)} /mo</strong>
                </div>
                <div className="flex justify-between">
                  <span>Estimator hours handed to agents</span>
                  <strong data-testid="roi-hours">{hours} hrs /mo</strong>
                </div>
              </div>
              <p className="text-xs text-stone-500 mt-6 leading-relaxed">
                Assumes 100% of short-paid ROs are answered at industry-typical
                win rates for documented, cited supplements. Recovery is pure
                margin — agents do the labor, and Brim is a flat subscription.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoiCalculator;
